<script setup>
import { inject, ref, watch } from 'vue'
import { apolloClient } from 'boot/apollo'
import { useMeta } from 'quasar'
import { useGlobalStore } from 'src/composables/useGlobalStore'
import termsQuery from 'src/graphql/termsQuery.gql'
import debounce from 'debounce'
import B2CContactPopup from 'components/home/B2CContactPopup.vue'
import AppButton from 'app/shared/general/AppButton.vue'
import gsap from 'gsap'
import AppDialog from 'app/shared/general/AppDialog.vue'
import TermsSelector from 'components/insights/TermsSelector.vue'

const { globalConfiguration } = useGlobalStore()

const groupedTerms = ref([])
const contactVisible = ref(false)
const searchText = ref(null)
const disabled = ref(false)
const { screen } = inject('Shared')

function closeAll (exceptionGroupIndex, exceptionTermIndex) {
  groupedTerms.value.forEach((group, groupIndex) => {
    if (groupIndex !== exceptionGroupIndex) {
      group.terms.forEach((term, termIndex) => {
        if (term.open && termIndex !== exceptionTermIndex) {
          toggleTerm(term, groupIndex, termIndex)
        }
      })
    }
  })
}

let loadTerms = async () => {
  try {
    closeAll()
    const termsData = await apolloClient.query({
      query: termsQuery,
      variables: {
        searchText: searchText.value
      }
    })
    const res = []
    JSON.parse(JSON.stringify(termsData?.data?.terms)).forEach(term => {
      const firstLetter = term.name[0]
      const existingItem = res.find(e => e.letter === firstLetter)
      term.open = false
      if (searchText.value) {
        const index = term.name.toLowerCase().indexOf(searchText.value.toLowerCase())
        if (index >= 0) {
          term.name = `<span class="highlight-low">${term.name.substring(0, index)}</span>` + "<span class='highlight'>" + term.name.substring(index, index + searchText.value.length) + '</span>' + `<span class="highlight-low">${term.name.substring(index + searchText.value.length)}</span>`
        }
      }
      if (!existingItem) {
        res.push({
          letter: firstLetter,
          terms: [term]
        })
      } else {
        existingItem.terms.push(term)
      }
    })
    groupedTerms.value = res
  } catch (e) {
    console.log('ERROR', e)
    console.log(JSON.parse(JSON.stringify(e)))
  }
}
loadTerms()
loadTerms = debounce(loadTerms, 200)
watch(searchText, loadTerms)

if (globalConfiguration.value && globalConfiguration.value.pages && globalConfiguration.value.pages.terms) {
  useMeta({
    title: globalConfiguration.value.pages.terms.title,
    meta: {
      description: { name: 'description', content: globalConfiguration.value.pages.terms.description },
      keywords: { name: 'keywords', content: globalConfiguration.value.pages.terms.keyword },
      canonical: { rel: 'canonical', href: 'https://raumpioniere.ch/glossary' },
      'og:title': { property: 'og:title', content: globalConfiguration.value.pages.terms.ogTitle ? globalConfiguration.value.pages.terms.ogTitle : globalConfiguration.value.pages.terms.title },
      'og:image': { property: 'og:image', content: globalConfiguration.value.pages.terms.ogImage ? globalConfiguration.value.pages.terms.ogImage : 'https://raumpioniere.ch/icons/favicon-128x128.png' },
      'og:url': { property: 'og:url', content: 'https://raumpioniere.ch/glossary' },
      'og:description': { property: 'og:description', content: globalConfiguration.value.pages.terms.ogDescription ? globalConfiguration.value.pages.terms.ogDescription : globalConfiguration.value.pages.terms.description },
      'og:type': { property: 'og:type', content: globalConfiguration.value.pages.terms.ogType ? globalConfiguration.value.pages.terms.ogType : 'Glossary' },
      'og:locale': { property: 'og:locale', content: 'de_CH' }
    }
  })
}

function toggleTerm (term, groupIndex, termIndex) {
  if (disabled.value) {
    setTimeout(() => {
      toggleTerm(term, groupIndex, termIndex)
    }, 500)
    return
  }
  const selector = `.terms--group-term-content-${groupIndex}-${termIndex}`
  if (term.open) {
    gsap.to(selector, { duration: 0.5, maxHeight: '0px', ease: 'ease', borderBottom: '0px solid transparent', onComplete: () => { disabled.value = false } })
  } else {
    closeAll(groupedTerms, termIndex)
    disabled.value = true
    gsap.to(selector, { duration: 1, maxHeight: '2000px', ease: 'ease', borderBottom: '1px solid #B4BCCE', onComplete: () => { disabled.value = false } })
  }
  term.open = !term.open
}

const selectedCTA = ref()
const showCTA = ref(false)
watch(showCTA, v => {
  contactVisible.value = false
})
</script>

<template lang="pug">
b2-c-contact-popup(v-model:visible="contactVisible" :always="true")
.terms
  .routerPageSize
    .terms-content
      .terms--header
        .terms--header-svg
          svg(width='160' height='202' viewBox='0 0 160 202' fill='none' xmlns='http://www.w3.org/2000/svg')
            path(d='M42.2136 137.946H45.9928L30.4882 104.033H26.9528L11.4482 137.946H15.1831L19.2393 128.891H38.1574L42.2136 137.946ZM20.536 125.976L28.6928 107.734L36.8496 125.976H20.5249H20.536Z' fill='#22223C')
            path(d='M56.4106 186.033L78.8308 157.695V155.224H51.9443V158.327H74.0763L51.6118 186.654V189.125H79.4182V186.033H56.4106Z' fill='#22223C')
            path(d='M1.5293 71.0952V199.488H88.8826V80.6152H11.0604C5.79611 80.6152 1.54038 76.3483 1.54038 71.0952C1.54038 65.8309 5.80719 61.5752 11.0604 61.5752H74.586' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
            path(d='M81.7345 71.0952H11.4595' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
            path(d='M116.999 107.734L156.974 157.063C159.28 159.911 158.847 164.09 155.999 166.395L150.702 170.695C147.853 173 143.675 172.568 141.37 169.72L101.395 120.391' fill='#1F47CD')
            path(d='M116.999 107.734L156.974 157.063C159.28 159.911 158.847 164.09 155.999 166.395L150.702 170.695C147.853 173 143.675 172.568 141.37 169.72L101.395 120.391' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
            path(opacity='0.75' d='M86.0279 111.108C104.139 107.898 116.216 90.6045 113.005 72.4818C109.793 54.359 92.5075 42.2696 74.3968 45.4793C56.2861 48.689 44.2082 65.9824 47.42 84.1051C50.6319 102.228 67.9172 114.317 86.0279 111.108Z' fill='#5FEEBF')
            path(d='M116.8 48.6193C100.42 28.4046 70.7515 25.3014 50.5478 41.6927C30.3442 58.0839 27.2521 87.7521 43.6323 107.967C60.0124 128.182 89.6806 131.285 109.884 114.893C130.088 98.5023 133.18 68.834 116.8 48.6193ZM101.195 104.177C86.91 115.769 65.9416 113.575 54.3492 99.278C42.7678 84.9814 44.9511 64.002 59.2366 52.4096C73.5221 40.8171 94.4905 43.0115 106.083 57.3081C117.664 71.6047 115.481 92.5841 101.195 104.177Z' fill='white' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
            path(d='M136.937 78.2876H158.459' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
            path(d='M120.313 38.1577L135.529 22.9302' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
            path(d='M80.1719 21.5336V0' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
            path(d='M40.0415 38.1577L24.814 22.9302' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
        .terms--header-details
          .terms--header-title Glossar
          .terms--header-subtitle Wer sich mit dem Bauen ohne Land beschäftigt, stösst immer wieder auf Fachbegriffe. Um ein gemeinsames Grundverständnis zu schaffen, erklären wir Sie Ihnen sehr gerne.
      .terms--input-container
        input.terms--input(v-model="searchText" name="searchText" placeholder="Wort suchen")
      .terms--groups
        .terms--group(v-for="(group, groupIndex) in groupedTerms" :key="groupIndex")
          .terms--group-letter {{ group.letter }}
          .terms--group-terms(v-for="(term, termIndex) in group.terms" :key="termIndex")
            .terms--group-term(v-html="term.name" @click="toggleTerm(term, groupIndex, termIndex)" :class="term.open ? 'terms--group-term-selected' : ''")
            .terms--group-term-content(:class="`terms--group-term-content-${groupIndex}-${termIndex}`")
              .terms--group-term-content-body
                .terms--group-term-content-body-content(v-html="term.body")
                app-button.terms--group-term-content-body-cta(
                  v-if="term.cta && term.cta.label && term.cta.to"
                  :label="term.cta.label"
                  @click="selectedCTA = term.cta; showCTA = !showCTA"
                )
              .terms--group-term-content-image-container
                img.terms--group-term-content-image(:src="`https://raumpionierestorage.blob.core.windows.net/images/${term.image}`")
                .terms--group-term-content-caption {{ term.imageCaption }}
    .terms--cta
      .terms--cta-title Alles unklar? Sie haben den gesuchten Begriff nicht gefunden, finden unsere Erklärung nicht schlüssig oder haben einen Tipp für uns Raumpioniere?
      .terms--cta-btn-container
        app-button.terms--cta-btn(@click="contactVisible = !contactVisible" label="Kontaktieren Sie uns")
  app-dialog(v-model:show-modal='showCTA' persistent position="center" full-screen maximized :fixed="true" filter-color="rgba(255, 255, 255, 0.5)")
    template(#modal-content)
      .yt--container
        .yt--close(@click="showCTA = false")
          svg(width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg')
            path(d='M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z' fill='#626C85')
        .video-container
          iframe(:src='selectedCTA.to' title='YouTube video player' frameborder='0' allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture' allowfullscreen='')
</template>

<style lang="sass">
.yt--container
  position: relative
  background: linear-gradient(152.99deg, #2F3849 78.27%, #21252E 157.29%)
  box-shadow: 0px 15px 30px rgba(166, 166, 166, 0.25)
  border-radius: 10px
  width: 860px
  max-width: 100vw
  display: flex
  justify-content: center
  align-items: center
  @media screen and (min-width: 1024px)
    margin-top: 90px
    padding: 30px
  @media screen and (max-width: 1023px)
    margin: 60px 20px 20px 20px
    padding: 10px
  .video-container
    width: 100%
    position: relative
    padding-bottom: 56.25% /* 16:9 */
    height: 0
  .video-container iframe
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    z-index: 1
  .yt--close
    cursor: pointer
    position: absolute
    top: 10px
    z-index: 2
    right: 10px
.terms
  position: relative
  @media screen and (min-width: 1024px)
    margin-top: 90px
  @media screen and (max-width: 1024px)
    padding: 0 10px
  .routerPageSize
    @media screen and (max-width: 1024px)
      padding-top: 0px !important
  .terms-content
    .terms--header
      @media screen and (min-width: 1024px)
        display: flex
        margin-bottom: 90px
      @media screen and (max-width: 1024px)
        margin-bottom: 50px
      .terms--header-svg
        @media screen and (min-width: 1024px)
          margin-right: 60px
        @media screen and (max-width: 1024px)
          display: flex
          justify-content: center
        svg
          @media screen and (min-width: 1024px)
            width: 160px
            height: 202px
          @media screen and (max-width: 1024px)
            width: 100px
            height: 125px
      .terms--header-details
        display: flex
        flex-direction: column
        justify-content: center
        .terms--header-title
          margin-bottom: 20px
          font-weight: 500
          line-height: 130%
          color: #22223C
          @media screen and (min-width: 1024px)
            font-size: 55px
          @media screen and (max-width: 1024px)
            font-size: 30px
            margin: 30px 0 20px
            display: flex
            justify-content: center
        .terms--header-subtitle
          font-weight: 400
          line-height: 150%
          color: #22223C
          max-width: 680px
          @media screen and (min-width: 1024px)
            font-size: 20px
          @media screen and (max-width: 1024px)
            font-size: 16px
            text-align: center
    .terms--input-container
      background: white
      position: -webkit-sticky
      position: sticky
      left: 0
      align-self: flex-start
      @media screen and (min-width: 1024px)
        padding: 20px 0 20px
        top: 90px
      @media screen and (max-width: 1024px)
        padding: 20px 0 20px
        top: 60px
      .terms--input
        width: 100%
        color: #B4BCCE
        border: 2px solid #1F47CD
        border-radius: 10px
        @media screen and (min-width: 1024px)
          height: 68px
          padding: 30px
          font-size: 24px
        @media screen and (max-width: 1024px)
          height: 58px
          padding: 14px 20px
          font-size: 16px
    .terms--groups
      @media screen and (min-width: 1024px)
        margin-top: 40px
      @media screen and (max-width: 1024px)
        margin-top: 15px
      .terms--group
        .terms--group-letter
          color: #1F47CD
          font-weight: 700
          line-height: 300%
          border-bottom: 2px solid #1F47CD
          @media screen and (min-width: 1024px)
            font-size: 24px
            padding-left: 25px
          @media screen and (max-width: 1024px)
            font-size: 16px
            padding-left: 10px
        .terms--group-terms
          .highlight-low
            color: #22223C80 !important
            font-weight: 500 !important
          .highlight
            font-weight: 700
          .terms--group-term-selected
            color: #007BFF !important
            border-bottom: none !important
          .terms--group-term
            transition: 0.3s color ease-in-out
            cursor: pointer
            line-height: 300%
            border-bottom: 1px solid #B4BCCE
            &:hover
              color: #1F47CD !important
            @media screen and (min-width: 1024px)
              padding-left: 25px
              font-size: 24px
            @media screen and (max-width: 1024px)
              padding-left: 10px
              font-size: 16px
          .terms--group-term-content
            max-height: 0px
            overflow: hidden
            @media screen and (min-width: 1024px)
              grid-gap: 105px
              display: grid
              grid-template-columns: 1fr 1fr
              padding-left: 25px
              margin-top: 10px
            @media screen and (max-width: 1024px)
              display: flex
              flex-direction: column-reverse
              grid-gap: 20px
              padding-left: 10px
              margin-top: 10px
            .terms--group-term-content-body
              @media screen and (min-width: 1024px)
                padding-bottom: 60px
              @media screen and (max-width: 1024px)
                padding-bottom: 20px
              .terms--group-term-content-body-content
                line-height: 150%
                color: #22223C
                @media screen and (min-width: 1024px)
                  font-size: 20px
                @media screen and (max-width: 1024px)
                  font-size: 16px
              .terms--group-term-content-body-cta
                width: 123px !important
                font-weight: 500 !important
                @media screen and (min-width: 1024px)
                  margin-top: 30px
                  font-size: 16px !important
                  height: 40px
                  min-height: 40px !important
                @media screen and (max-width: 1024px)
                  margin-top: 20px
                  font-size: 14px !important
                  height: 35px
                  min-height: 35px !important
            .terms--group-term-content-image-container
              max-width: 100%
              @media screen and (min-width: 1024px)
                padding-bottom: 60px
              @media screen and (max-width: 1024px)
                padding-bottom: 30px
              img
                max-width: 100%
              .terms--group-term-content-image
                @media screen and (min-width: 1024px)
                  margin-bottom: 41px
                @media screen and (max-width: 1024px)
                  margin-bottom: 10px
              .terms--group-term-content-caption
                font-weight: 400
                line-height: 150%
                color: #22223C
                @media screen and (min-width: 1024px)
                  font-size: 16px
                @media screen and (max-width: 1024px)
                  font-size: 12px
  .terms--cta
    display: flex
    flex-direction: column
    align-items: center
    @media screen and (min-width: 1024px)
      margin-top: 100px
    @media screen and (max-width: 1024px)
      margin-top: 50px
    .terms--cta-title
      font-weight: 500
      line-height: 130%
      text-align: center
      color: #22223C
      max-width: 900px
      @media screen and (min-width: 1024px)
        margin-bottom: 40px
        font-size: 34px
      @media screen and (max-width: 1024px)
        margin-bottom: 30px
        font-size: 24px
  .terms--cta-btn-container
    width: 100%
    display: flex
    justify-content: center
    @media screen and (min-width: 1024px)
      margin-bottom: 135px
    @media screen and (max-width: 1024px)
      margin-bottom: 50px
    .terms--cta-btn
      font-weight: 600
      width: 360px
</style>
